import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Share2Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import {
    Dialog,
    DialogBody,
    DialogDescription,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import useDialog from '@/hooks/useDialog';
import server from '@/services/server';
import useClubErrors from '../hooks/useClubErrors';
import useClubPortalContainer from '../hooks/useClubPortalContainer';
import ClubSettingsCardHeader from './ClubSettingsCardHeader';

const optionalUrl = z
    .string()
    .trim()
    .optional()
    .or(z.literal(''))
    .refine(value => !value || /^https?:\/\//i.test(value) || /^@?[a-z0-9._-]+$/i.test(value), {
        message: 'Informe uma URL (https://…) ou um @usuario',
    });

const socialSchema = z.object({
    facebook: optionalUrl,
    instagram: optionalUrl,
    tiktok: optionalUrl,
});

/** Normaliza handle (@user) ou URL para link absoluto. */
export function normalizeClubSocialUrl(value, platform) {
    const raw = String(value || '').trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;

    const handle = raw.replace(/^@/, '').replace(/^\/+/, '');
    if (!handle) return null;

    if (platform === 'instagram') return `https://www.instagram.com/${handle}`;
    if (platform === 'facebook') return `https://www.facebook.com/${handle}`;
    if (platform === 'tiktok') return `https://www.tiktok.com/@${handle}`;
    return `https://${handle}`;
}

function statusLabel(value) {
    return value?.trim() ? 'Configurado' : 'Não informado';
}

function DialogSocialLinks({ data, mutate, open, onOpenChange }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const portalContainer = useClubPortalContainer();

    const form = useForm({
        resolver: zodResolver(socialSchema),
        defaultValues: {
            facebook: '',
            instagram: '',
            tiktok: '',
        },
    });

    useEffect(() => {
        if (!open || !data) return;
        form.reset({
            facebook: data.facebook || '',
            instagram: data.instagram || '',
            tiktok: data.tiktok || '',
        });
    }, [open, data, form]);

    const onSubmit = async values => {
        try {
            await server.put('club', {
                facebook: normalizeClubSocialUrl(values.facebook, 'facebook'),
                instagram: normalizeClubSocialUrl(values.instagram, 'instagram'),
                tiktok: normalizeClubSocialUrl(values.tiktok, 'tiktok'),
            });
            await mutate();
            enqueueSnackbar('Redes sociais salvas');
            onOpenChange(false);
        } catch (error) {
            setErrors(error);
        }
    };

    const { errors, isSubmitting } = form.formState;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <ScrollDialogContent container={portalContainer} className="sm:max-w-lg">
                <ScrollDialogHeader>
                    <DialogTitle>Redes sociais</DialogTitle>
                    <DialogDescription>
                        Links exibidos nos ícones do rodapé do site do clube. Use a URL completa ou o @usuario.
                    </DialogDescription>
                </ScrollDialogHeader>

                <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
                    <DialogBody>
                        <FieldGroup>
                            <Field data-invalid={Boolean(errors.facebook)}>
                                <FieldLabel htmlFor="club-facebook">Facebook</FieldLabel>
                                <Input
                                    id="club-facebook"
                                    placeholder="https://facebook.com/seuclube ou @seuclube"
                                    {...form.register('facebook')}
                                    aria-invalid={Boolean(errors.facebook)}
                                />
                                <FieldError>{errors.facebook?.message}</FieldError>
                            </Field>

                            <Field data-invalid={Boolean(errors.instagram)}>
                                <FieldLabel htmlFor="club-instagram">Instagram</FieldLabel>
                                <Input
                                    id="club-instagram"
                                    placeholder="https://instagram.com/seuclube ou @seuclube"
                                    {...form.register('instagram')}
                                    aria-invalid={Boolean(errors.instagram)}
                                />
                                <FieldError>{errors.instagram?.message}</FieldError>
                            </Field>

                            <Field data-invalid={Boolean(errors.tiktok)}>
                                <FieldLabel htmlFor="club-tiktok">TikTok</FieldLabel>
                                <Input
                                    id="club-tiktok"
                                    placeholder="https://tiktok.com/@seuclube ou @seuclube"
                                    {...form.register('tiktok')}
                                    aria-invalid={Boolean(errors.tiktok)}
                                />
                                <FieldDescription>Só os links preenchidos aparecem no rodapé.</FieldDescription>
                                <FieldError>{errors.tiktok?.message}</FieldError>
                            </Field>
                        </FieldGroup>
                    </DialogBody>

                    <ScrollDialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? <Spinner className="size-4" /> : 'Salvar'}
                        </Button>
                    </ScrollDialogFooter>
                </form>
            </ScrollDialogContent>
        </Dialog>
    );
}

export default function SocialLinksClub({ data, mutate }) {
    const { open, handleClose } = useDialog('#redes-sociais');
    const hasAny = Boolean(data?.facebook || data?.instagram || data?.tiktok);

    return (
        <>
            <Card>
                <CardHeader>
                    <ClubSettingsCardHeader
                        icon={Share2Icon}
                        title="Redes sociais"
                        description="Facebook, Instagram e TikTok no rodapé do site do clube."
                    />
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                    <div className="flex flex-wrap gap-4">
                        <div>
                            <span className="text-muted-foreground">Facebook</span>
                            <p className="font-medium">{statusLabel(data?.facebook)}</p>
                        </div>
                        <div>
                            <span className="text-muted-foreground">Instagram</span>
                            <p className="font-medium">{statusLabel(data?.instagram)}</p>
                        </div>
                        <div>
                            <span className="text-muted-foreground">TikTok</span>
                            <p className="font-medium">{statusLabel(data?.tiktok)}</p>
                        </div>
                    </div>
                </CardContent>
                <CardFooter>
                    <Button variant="outline" size="sm" render={<Link to="#redes-sociais" />}>
                        <Share2Icon className="size-4" />
                        {hasAny ? 'Editar' : 'Configurar redes'}
                    </Button>
                </CardFooter>
            </Card>

            <DialogSocialLinks
                data={data}
                mutate={mutate}
                open={open}
                onOpenChange={isOpen => !isOpen && handleClose()}
            />
        </>
    );
}
